import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { IconCheck, IconCopy } from '@/icons'
import { log } from '@/lib/log'

/** An id, with a control that copies it. Ids are the currency of an admin panel. */
export function Copyable({ value, className }: { value: string; className?: string }) {
  const { t } = useTranslation()
  const [hasCopied, setHasCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setHasCopied(true)
      setTimeout(() => { setHasCopied(false) }, 1600)
    } catch (cause) {
      // Denied permission or an insecure origin. Not worth a toast; the value is on screen.
      log.exception('Could not write to the clipboard', cause)
    }
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        void copy()
      }}
      aria-label={hasCopied ? t('common.copied') : t('common.copyId')}
      className={cn(
        'group inline-flex items-center gap-1 rounded font-mono text-xs',
        'text-text-muted transition-colors hover:text-text',
        className,
      )}
    >
      <span className="truncate">{value}</span>
      {hasCopied ? (
        <IconCheck size={12} className="shrink-0 text-success" />
      ) : (
        <IconCopy size={12} className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
      )}
    </button>
  )
}
